import * as Fingerprint from '@fingerprint/agent';
import { IS_E2E, IS_TEST } from '@onefootprint/dev-tools';
import { postHostedOnboardingFp } from '@onefootprint/fetch-hosted';
import type { FingerprintVisitPath } from '@onefootprint/request-types';
import { getLogger } from '@/idv2/utils/logger';

const { logInfo, logWarn } = getLogger({ name: 'idv2', location: 'submit-fingerprint' });

type FingerprintData = {
  visitorId: string;
  requestId: string;
};

const apiKey = process.env.NEXT_PUBLIC_FINGERPRINT_API_KEY;
const envPath = process.env.NEXT_PUBLIC_FINGERPRINT_PATH;
const isDisabled = IS_TEST || IS_E2E;

const fetchFingerprintData = async (key: string): Promise<FingerprintData> => {
  const client = Fingerprint.start({ apiKey: key, endpoints: 'https://fp.risk.onefootprint.com' });
  const result = await client.get();
  return {
    visitorId: result.visitor_id ?? '',
    requestId: result.event_id,
  };
};

const loadAndSubmit = async (options: { authToken: string; path: FingerprintVisitPath; documentId?: string }) => {
  try {
    if (!apiKey) {
      logWarn('No fingerprint API key');
      return;
    }
    const data = await fetchFingerprintData(apiKey);
    await postHostedOnboardingFp({
      body: {
        visitorId: data.visitorId,
        requestId: data.requestId,
        path: options.path,
        documentId: options.documentId,
      },
      headers: {
        'X-Fp-Authorization': options.authToken,
      },
    });
    logInfo('submitFingerprint success', { path: options.path, documentId: options.documentId });
  } catch (error) {
    logWarn('Error fetching fingerprint data:', error);
  }
};

const getPath = (path: string | undefined): path is FingerprintVisitPath => {
  return path === 'onboard_start' || path === 'handoff' || path === 'identity_document_start';
};

// Fire-and-forget: fingerprint collection is non-blocking and must never propagate errors
const submitFingerprint = (options: { authToken: string; path: FingerprintVisitPath; documentId?: string }) => {
  const path = options.path || envPath;
  const authToken = options.authToken;
  const documentId = options.documentId;

  if (isDisabled) {
    return;
  }
  if (authToken && getPath(path)) {
    loadAndSubmit({ path, authToken, documentId }).catch(() => {});
  }
};

export default submitFingerprint;
